/**
 * Firm KPI Cards - Summary metrics for MFO firm-wide dashboard.
 * Shows aggregate metrics across all families.
 */

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import {
  TrendingUp,
  TrendingDown,
} from "lucide-react";

interface FirmKpiCardsProps {
  metrics: {
    totalAum: number | null;
    familyCount: number;
    aumChange: number | null;
    documentsPending: number;
    staleNavCount: number;
    openActionsCount: number;
  };
}

const formatCurrency = (value: number | null | undefined): string => {
  if (value == null) return '-';
  if (value >= 1e9) {
    return `$${(value / 1e9).toFixed(2)}B`;
  }
  if (value >= 1e6) {
    return `$${(value / 1e6).toFixed(1)}M`;
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export function FirmKpiCards({ metrics }: FirmKpiCardsProps) {
  const navigate = useNavigate();

  return (
    <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      {/* Total AUM */}
      <Card
        className="cursor-pointer hover:border-primary transition-colors col-span-1 xl:col-span-2"
        onClick={() => navigate('/firm/families')}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Firm AUM</CardTitle>
        </CardHeader>
        <CardContent>
          {metrics.totalAum !== null ? (
            <>
              <div className="text-2xl font-bold">{formatCurrency(metrics.totalAum)}</div>
              {metrics.aumChange !== null && (
                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                  {metrics.aumChange > 0 ? (
                    <TrendingUp className="h-3 w-3 text-success" />
                  ) : (
                    <TrendingDown className="h-3 w-3 text-destructive" />
                  )}
                  <span className={metrics.aumChange > 0 ? "text-success" : "text-destructive"}>
                    {metrics.aumChange > 0 ? "+" : ""}{metrics.aumChange.toFixed(1)}%
                  </span>
                  <span>YTD</span>
                </p>
              )}
            </>
          ) : (
            <>
              <div className="text-2xl font-bold text-muted-foreground">N/A</div>
              <p className="text-xs text-muted-foreground mt-1">No position data</p>
            </>
          )}
        </CardContent>
      </Card>

      {/* Family Count */}
      <Card
        className="cursor-pointer hover:border-primary transition-colors"
        onClick={() => navigate('/firm/families')}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Families</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{metrics.familyCount}</div>
          <p className="text-xs text-muted-foreground mt-1">Active clients</p>
        </CardContent>
      </Card>

      {/* Documents Pending */}
      <Card
        className="cursor-pointer hover:border-primary transition-colors"
        onClick={() => navigate('/data/review')}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Pending Docs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className={`text-2xl font-bold ${metrics.documentsPending > 0 ? 'text-warning' : ''}`}>
            {metrics.documentsPending}
          </div>
          <p className="text-xs text-muted-foreground mt-1">Awaiting review</p>
        </CardContent>
      </Card>

      {/* Stale NAVs */}
      <Card
        className="cursor-pointer hover:border-primary transition-colors"
        onClick={() => navigate('/portfolio/valuations')}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Stale NAVs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className={`text-2xl font-bold ${metrics.staleNavCount > 0 ? 'text-destructive' : 'text-success'}`}>
            {metrics.staleNavCount}
          </div>
          <p className="text-xs text-muted-foreground mt-1">{'>'}30 days old</p>
        </CardContent>
      </Card>

      {/* Open Actions */}
      <Card
        className="cursor-pointer hover:border-primary transition-colors"
        onClick={() => navigate('/crm/actions')}
      >
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Open Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className={`text-2xl font-bold ${metrics.openActionsCount > 10 ? 'text-warning' : ''}`}>
            {metrics.openActionsCount}
          </div>
          <p className="text-xs text-muted-foreground mt-1">To complete</p>
        </CardContent>
      </Card>
    </div>
  );
}
